import React, { useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
const { Panel: DialogPanel, Title: DialogTitle } = Dialog;
const { Child: TransitionChild } = Transition;
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { useGiveNotice } from '@/hooks/trades';
import { useToastStore } from '@/stores';
import { getErrorMessage } from '@/utils/get-error-message';
import { formatDate, localDateKeyPlusDays, pluralizeUnit, todayLocalKey } from '@/utils/format';
// Import canPerform from its specific module, NOT the @/features/trades/hooks
// barrel — the barrel pulls the whole hooks tree (useTradeActions → inventory
// stores), which over-mocking-sensitive modal unit tests can't satisfy.
import { canPerform, GIVE_NOTICE_NO_PERIOD_TOOLTIP } from '@/features/trades/hooks/trade-actions';
import type { PositionDetail } from '@/types';

interface NoticeModalProps {
  isOpen: boolean;
  onClose: () => void;
  trade: PositionDetail;
  /**
   * The `useGiveNotice` mutation handle composed by the page-level
   * `useTradeActions` (LR-208 r3 M4). Pre-fix the modal self-hosted
   * `useGiveNotice()`, creating a second TanStack Query subscription distinct
   * from the page's. Receiving the handle via props makes the page's
   * useTradeActions the single mutation owner.
   */
  giveNotice: ReturnType<typeof useGiveNotice>;
}

/**
 * Default notice message — what the back-office will send to the counterparty
 * when no per-trade override exists. Surfaced verbatim as a read-only preview
 * so the trader sees the actual wording, not an editable placeholder that
 * doesn't reach the wire (LR-208 r3 H3).
 */
const DEFAULT_NOTICE_MESSAGE =
  'Please be advised that we intend to recall the securities on the effective date noted above.';

/**
 * Trade-detail notice dialog. The submit-path currently posts `{ id, noticeDate
 * = today }` only — neither the notice-period override nor the message text are
 * accepted by the API. Pre-H3 the dialog rendered EDITABLE inputs for both,
 * which silently discarded the trader's edits at submit (a legal-effect form
 * where the visible "Message" textarea never reached the counterparty); this
 * version surfaces the values that will be sent as READ-ONLY previews, so the
 * UI matches the wire.
 *
 * A backend extension that accepts a per-trade `notice_period` / `message`
 * override is tracked as a follow-up; once the API + `useGiveNotice` accept
 * those fields, the previews can be restored to inputs.
 */
export function NoticeModal({ isOpen, onClose, trade, giveNotice }: NoticeModalProps) {
  // Notice period — from the trade's agreement-set value, never an editable
  // override. `notice_period_days` may be missing on legacy / freshly-booked
  // rows; surface "—" rather than fabricating a default that the back office
  // wouldn't actually use.
  const noticePeriod = trade.notice_period_days ?? null;
  const noticePeriodLabel =
    noticePeriod != null ? pluralizeUnit(noticePeriod, 'day') : '—';

  // Effective date — today + notice period. Display-only, NOT submitted (the
  // back office derives it server-side from `noticeDate` + the agreement
  // notice period). Local-zone day key so the preview matches the trader's
  // clock; mirrors the noticeDate submission (LR-208 r5 M1).
  const effectiveDate = useMemo(() => {
    if (!isOpen || noticePeriod == null) return null;
    return localDateKeyPlusDays(noticePeriod);
  }, [isOpen, noticePeriod]);

  const { mutate, isPending } = giveNotice;
  const { addToast } = useToastStore();

  const handleSubmit = () => {
    // Submit-path guard (ACTN-02 / "guard the full action path", LR-208 r6 LOW):
    // re-check validity against the freshly-loaded trade before firing the
    // mutation so a stale/keyboard/race submit no-ops — mirrors
    // SettlementDateModal. give_notice is only valid for an evergreen trade with
    // notice not yet served.
    if (!canPerform(trade, 'give_notice')) return;
    mutate(
      // LR-208 r5 M1: local-zone day key, NOT new Date().toISOString().split('T')[0]
      // (UTC slice). The latter silently emits "tomorrow UTC" as a legal-effect
      // notice date from UTC-negative evening clicks.
      { id: trade.id, noticeDate: todayLocalKey() },
      {
        onSuccess: () => {
          addToast('success', 'Notice sent successfully');
          onClose();
        },
        onError: (error) => {
          addToast('error', getErrorMessage(error, 'Failed to send notice'));
        },
      }
    );
  };

  return (
    <Transition show={isOpen}>
      <Dialog onClose={onClose} className="relative z-[100]">
        <TransitionChild enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </TransitionChild>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
            <DialogPanel className="w-full max-w-md bg-white rounded-xl shadow-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <DialogTitle className="text-lg font-semibold text-gray-900">Send Notice</DialogTitle>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
              </div>

              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Send recall notice for position <strong>TRD-{trade.id}</strong>{' '}
                  ({trade.symbol_id}). The values below will be sent to the back office.
                </p>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                      Notice Period
                    </p>
                    <p className="text-sm font-medium text-gray-900">{noticePeriodLabel}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">from agreement</p>
                  </div>
                  <div>
                    <p className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                      Effective Date
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {effectiveDate ? formatDate(effectiveDate) : '—'}
                    </p>
                    <p className="text-[11px] text-gray-500 mt-0.5">today + notice period</p>
                  </div>
                </div>

                {/* LR-478 S3: after-state preview — serving notice converts an
                    evergreen (open) position into a term one maturing on the
                    effective date. Shown only once the effective date is known. */}
                {effectiveDate && (
                  <div
                    role="note"
                    aria-live="polite"
                    className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700"
                  >
                    On confirm this position converts{' '}
                    <strong className="text-gray-900">Evergreen</strong> →{' '}
                    <strong className="text-gray-900">Term</strong> · maturity{' '}
                    <strong className="text-gray-900">{formatDate(effectiveDate)}</strong>.
                  </div>
                )}

                <div>
                  <p className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                    Message
                  </p>
                  <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    {DEFAULT_NOTICE_MESSAGE}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1">
                    Standard notice text. Per-trade overrides are not yet supported on this dialog.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button
                  onClick={handleSubmit}
                  // LR-678: no notice period ⇒ the backend cannot derive the
                  // maturity date and 400s. The action panel already withholds
                  // Give Notice for such a trade; this is the same gate on the
                  // dialog itself, so no entry path reaches a doomed Confirm.
                  disabled={isPending || noticePeriod == null}
                  title={noticePeriod == null ? GIVE_NOTICE_NO_PERIOD_TOOLTIP : undefined}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
                >
                  {isPending ? 'Sending...' : 'Send Notice'}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
